import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, Calendar, CheckCircle, XCircle, ChevronDown, ChevronUp, 
  BookOpen, Award, ChevronLeft, ChevronRight, BarChart3, FileText, Clock,
  Filter, X
} from 'lucide-react';
import { leitnerService } from '../../services/leitnerService';
import { UserService } from '../../services/userService';

const PerformanceSummaryView = ({ bookId, tests, results, userId = null, isLearnerView = false }) => {
  const [learnersData, setLearnersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLearner, setExpandedLearner] = useState(null);
  const [userProfiles, setUserProfiles] = useState({});
  const [selectedWeekStart, setSelectedWeekStart] = useState(getMonday(new Date()));
  const [viewMode, setViewMode] = useState('week');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  
  // Curriculum filters
  const [filters, setFilters] = useState({ subject: null, grade: null, topics: [] });
  const [categories, setCategories] = useState({ subjects: [], grades: [], topics: [] });
  const [showFilters, setShowFilters] = useState(false);
  
  // Cache question metadata
  const questionMetaMapRef = useRef(null);

  useEffect(() => {
    if (bookId) {
      loadCategories();
    }
  }, [bookId]);

  useEffect(() => {
    if (bookId) loadLearnersProgress();
  }, [bookId, selectedWeekStart, viewMode, customRange, filters, results]);

  const loadCategories = async () => {
    try {
      const cats = await leitnerService.getQuestionCategories(bookId);
      setCategories(cats);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  function getMonday(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function getWeekBoundaries(mondayDate) {
    const monday = new Date(mondayDate);
    monday.setHours(0, 0, 0, 0);
    
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    friday.setHours(23, 59, 59, 999);
    
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    
    const now = new Date();
    const isCurrentWeek = now >= monday && now <= sunday;
    const isWeekend = now.getDay() === 0 || now.getDay() === 6;
    
    return { monday, friday, sunday, isCurrentWeek, isWeekend };
  }

  function getSelectedPeriod() {
    if (viewMode === 'custom' && customRange.start && customRange.end) {
      const start = new Date(customRange.start);
      start.setHours(0, 0, 0, 0);
      const end = new Date(customRange.end);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    const bounds = getWeekBoundaries(selectedWeekStart);
    return { start: bounds.monday, end: bounds.friday };
  }

  function applyCurriculumFilters(items, filters) {
    return items.filter(item => {
      if (filters.subject && item.subject !== filters.subject) return false;
      if (filters.grade && item.grade !== filters.grade) return false;
      if (filters.topics?.length > 0) {
        const itemTopics = item.topics || [];
        if (!filters.topics.some(t => itemTopics.includes(t))) return false;
      }
      return true;
    });
  }

  function getStatsForPeriod(items, start, end) {
    const stats = { answered: 0, correct: 0, attempts: 0, correctAttempts: 0, byDay: {} };
    
    const current = new Date(start);
    while (current <= end) {
      const dayKey = current.toISOString().split('T')[0];
      stats.byDay[dayKey] = { answered: 0, correct: 0, attempts: 0, correctAttempts: 0 };
      current.setDate(current.getDate() + 1);
    }
    
    items.forEach(item => {
      if (!item.lastReviewed) return;
      const reviewDate = item.lastReviewed.toDate ? item.lastReviewed.toDate() : new Date(item.lastReviewed);
      
      if (reviewDate >= start && reviewDate <= end) {
        const dayKey = reviewDate.toISOString().split('T')[0];
        if (stats.byDay[dayKey]) {
          stats.byDay[dayKey].answered++;
          stats.byDay[dayKey].attempts += item.attempts || 0;
          stats.byDay[dayKey].correctAttempts += item.correctAttempts || 0;
          stats.answered++;
          stats.attempts += item.attempts || 0;
          stats.correctAttempts += item.correctAttempts || 0;
          if (item.box > 1) {
            stats.byDay[dayKey].correct++;
          }
        }
      }
    });
    
    return stats;
  }

  function getTestResultsForPeriod(allResults, start, end, userId = null) {
    return (allResults || []).filter(result => {
      if (userId && result.userId !== userId) return false;
      const takenAt = result.takenAt?.toDate ? result.takenAt.toDate() : new Date(result.takenAt);
      return takenAt >= start && takenAt <= end;
    });
  }

  const loadLearnersProgress = async () => {
    setLoading(true);
    try {
      // Fetch or use cached question metadata
      if (!questionMetaMapRef.current) {
        questionMetaMapRef.current = await leitnerService.getQuestionMetaMap(bookId);
      }
      const questionMetaMap = questionMetaMapRef.current;

      let allProgress;
      
      if (isLearnerView && userId) {
        // Single learner mode - fetch only current user's progress
        const { questions = {}, subquestions = {} } = await leitnerService.getLearnerProgress(bookId, userId);
        allProgress = [{
          userId,
          questions: Object.entries(questions).map(([id, data]) => ({ id, ...data })),
          subquestions: Object.entries(subquestions).map(([id, data]) => ({ id, ...data }))
        }];
      } else {
        // Admin mode - fetch all learners
        allProgress = await leitnerService.getAllLearnersProgress(bookId);
      }
      
      const profiles = {};
      await Promise.all(
        allProgress.map(async (learner) => {
          try {
            const profile = await UserService.getUserProfile(learner.userId);
            profiles[learner.userId] = profile;
          } catch (e) {
            profiles[learner.userId] = null;
          }
        })
      );
      setUserProfiles(profiles);

      const period = getSelectedPeriod();
      
      const learnersWithStats = allProgress.map(learner => {
        // Enrich items with curriculum metadata
        const allItems = [
          ...learner.questions.map(item => {
            const meta = questionMetaMap[item.id] || {};
            return { ...item, subject: meta.subject, grade: meta.grade, topics: meta.topics };
          }),
          ...learner.subquestions.map(item => {
            const meta = questionMetaMap[item.parentQuestionId] || {};
            return { ...item, subject: meta.subject, grade: meta.grade, topics: meta.topics };
          })
        ];

        // Apply curriculum filters
        const filteredItems = applyCurriculumFilters(allItems, filters);
        
        const periodStats = getStatsForPeriod(filteredItems, period.start, period.end);
        
        const totalAttempts = filteredItems.reduce((sum, item) => sum + (item.attempts || 0), 0);
        const totalCorrect = filteredItems.reduce((sum, item) => sum + (item.correctAttempts || 0), 0);
        const accuracy = totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0;
        
        const boxStats = { 1: 0, 2: 0, 3: 0, 4: 0 };
        filteredItems.forEach(item => {
          const box = item.box || 1;
          if (boxStats[box] !== undefined) boxStats[box]++;
        });

        // Get test results for this learner in period
        const learnerTestResults = getTestResultsForPeriod(results, period.start, period.end, learner.userId);
        const testStats = {
          count: learnerTestResults.length,
          avgScore: learnerTestResults.length > 0 
            ? Math.round(learnerTestResults.reduce((sum, r) => sum + (r.score || 0), 0) / learnerTestResults.length) 
            : 0,
          totalQuestions: learnerTestResults.reduce((sum, r) => sum + (r.totalQuestions || 0), 0),
          totalCorrect: learnerTestResults.reduce((sum, r) => sum + (r.correctAnswers || 0), 0),
          results: learnerTestResults
        };

        return { ...learner, periodStats, totalAttempts, totalCorrect, accuracy, boxStats, totalQuestions: filteredItems.length, testStats };
      });

      setLearnersData(learnersWithStats);
    } catch (error) {
      console.error('Error loading learners progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedWeekStart(newDate);
  };

  const clearFilters = () => {
    setFilters({ subject: null, grade: null, topics: [] });
  };

  const hasActiveFilters = filters.subject || filters.grade || filters.topics.length > 0;

  const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const getDayName = (dateString) => new Date(dateString).toLocaleDateString('en-US', { weekday: 'short' });

  const weekBounds = getWeekBoundaries(selectedWeekStart);
  const period = getSelectedPeriod();
  const totals = {
    answered: learnersData.reduce((sum, l) => sum + l.periodStats.answered, 0),
    correct: learnersData.reduce((sum, l) => sum + l.periodStats.correct, 0),
    attempts: learnersData.reduce((sum, l) => sum + l.periodStats.attempts, 0),
    correctAttempts: learnersData.reduce((sum, l) => sum + l.periodStats.correctAttempts, 0)
  };

  const periodTestResults = getTestResultsForPeriod(results, period.start, period.end);
  const testTotals = {
    count: periodTestResults.length,
    avgScore: periodTestResults.length > 0 
      ? Math.round(periodTestResults.reduce((sum, r) => sum + (r.score || 0), 0) / periodTestResults.length) 
      : 0
  };

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="bg-white rounded-xl border shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${viewMode === 'week' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              Weekly
            </button>
            <button
              onClick={() => setViewMode('custom')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${viewMode === 'custom' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              Custom Range
            </button>
          </div>

          {viewMode === 'week' ? (
            <div className="flex items-center gap-3">
              <button onClick={() => navigateWeek(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="text-center min-w-[200px]">
                <p className="font-semibold text-gray-900">{formatDate(weekBounds.monday)} - {formatDate(weekBounds.friday)}</p>
                <p className="text-xs text-gray-500">{weekBounds.isCurrentWeek ? 'Current Week' : 'Past Week'}</p>
              </div>
              <button onClick={() => navigateWeek(1)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customRange.start}
                onChange={(e) => setCustomRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-1.5 border rounded-lg text-sm"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customRange.end}
                onChange={(e) => setCustomRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-1.5 border rounded-lg text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Curriculum Filters */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-indigo-600" />
            <span className="font-medium text-gray-900">Curriculum Filters</span>
            {hasActiveFilters && (
              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                Active
              </span>
            )}
          </div>
          {showFilters ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </button>

        {showFilters && (
          <div className="px-4 pb-4 border-t bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
              {/* Subject Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <select
                  value={filters.subject || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, subject: e.target.value || null }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Subjects</option>
                  {categories.subjects.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              {/* Grade Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                <select
                  value={filters.grade || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, grade: e.target.value || null }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Grades</option>
                  {categories.grades.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              {/* Topics Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Topics</label>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value && !filters.topics.includes(e.target.value)) {
                      setFilters(prev => ({ ...prev, topics: [...prev.topics, e.target.value] }));
                    }
                  }}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Add Topic...</option>
                  {categories.topics.filter(t => !filters.topics.includes(t)).map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Selected Topics */}
            {filters.topics.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {filters.topics.map(topic => (
                  <span key={topic} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                    {topic}
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, topics: prev.topics.filter(t => t !== topic) }))}
                      className="hover:text-indigo-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-3 text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Clear All Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Period Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={BarChart3} label="Total Attempts" value={totals.attempts} />
        <StatCard icon={CheckCircle} label="Correct" value={totals.correctAttempts} color="green" />
        <StatCard icon={XCircle} label="Incorrect" value={totals.attempts - totals.correctAttempts} color="red" />
        <StatCard icon={FileText} label="Questions Touched" value={totals.answered} />
      </div>

      {/* Weekend Tests */}
      {tests?.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b bg-amber-50">
            <h3 className="font-semibold text-amber-900 flex items-center gap-2">
              <Award className="w-5 h-5" />
              Weekend Tests
            </h3>
          </div>
          <div className="divide-y">
            {tests.map(test => (
              <div key={test.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">{test.name}</p>
                  <p className="text-sm text-gray-500">{test.questionIds?.length || 0} questions</p>
                </div>
                <Award className="w-5 h-5 text-amber-500" />
              </div>
            ))}
          </div>
          {weekBounds.isCurrentWeek && weekBounds.isWeekend && (
            <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
              <p className="text-sm text-amber-800 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Weekend Test Period Active - Learners can take tests now
              </p>
            </div>
          )}
        </div>
      )}

      {/* Learners List */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-600" />
            {isLearnerView ? 'My Progress' : 'Learner Progress'}
            {hasActiveFilters && <span className="text-sm font-normal text-gray-500">(filtered)</span>}
          </h3>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : learnersData.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No learners have started yet</p>
          </div>
        ) : (
          <div className="divide-y">
            {learnersData.map((learner) => {
              const profile = userProfiles[learner.userId];
              const isExpanded = expandedLearner === learner.userId;
              const displayName = profile?.displayName || profile?.email || learner.userId;
              const periodAccuracy = learner.periodStats.attempts > 0 
                ? Math.round((learner.periodStats.correctAttempts / learner.periodStats.attempts) * 100) 
                : 0;

              return (
                <div key={learner.userId}>
                  <button
                    onClick={() => setExpandedLearner(isExpanded ? null : learner.userId)}
                    className="w-full px-4 py-4 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-600 font-semibold">{displayName[0].toUpperCase()}</span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{displayName}</p>
                          <p className="text-sm text-gray-500">{learner.totalQuestions} questions · {learner.accuracy}% overall</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right hidden md:block">
                          <p className="text-sm font-medium">Period: {learner.periodStats.attempts} attempts</p>
                          <p className="text-xs">
                            <span className="text-green-600">{learner.periodStats.correctAttempts}✓</span>
                            {' · '}
                            <span className="text-red-600">{learner.periodStats.attempts - learner.periodStats.correctAttempts}✗</span>
                            {' · '}
                            <span className="text-indigo-600">{periodAccuracy}%</span>
                          </p>
                        </div>
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 bg-gray-50 space-y-4">
                      {/* Daily Breakdown */}
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Daily Breakdown</p>
                        <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-7 gap-2">
                          {Object.entries(learner.periodStats.byDay).map(([date, dayStats]) => (
                            <div key={date} className="bg-white p-3 rounded-lg border text-center">
                              <p className="text-xs text-gray-500 mb-1">{getDayName(date)}</p>
                              <p className="text-xs text-gray-400">{date.slice(5)}</p>
                              <p className="text-lg font-bold text-gray-900 mt-1">{dayStats.attempts}</p>
                              <div className="flex justify-center gap-2 text-xs mt-1">
                                <span className="text-green-600">{dayStats.correctAttempts}✓</span>
                                <span className="text-red-600">{dayStats.attempts - dayStats.correctAttempts}✗</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Period Summary */}
                      <div className="grid grid-cols-4 gap-3 p-4 bg-indigo-50 rounded-lg">
                        <div className="text-center">
                          <p className="text-xs text-indigo-600 font-medium">Questions</p>
                          <p className="text-xl font-bold text-indigo-900">{learner.periodStats.answered}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-indigo-600 font-medium">Attempts</p>
                          <p className="text-xl font-bold text-indigo-900">{learner.periodStats.attempts}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-green-600 font-medium">Correct</p>
                          <p className="text-xl font-bold text-green-600">{learner.periodStats.correctAttempts}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-indigo-600 font-medium">Accuracy</p>
                          <p className="text-xl font-bold text-indigo-900">{periodAccuracy}%</p>
                        </div>
                      </div>

                      {/* Learner Test Scores */}
                      {learner.testStats?.count > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                            <Award className="w-4 h-4 text-emerald-600" />
                            Tests Taken: {learner.testStats.count} · Avg Score: {learner.testStats.avgScore}%
                          </p>
                          <div className="space-y-2">
                            {learner.testStats.results.map((result, idx) => {
                              const testName = tests?.find(t => t.id === result.testId)?.name || 'Test';
                              const takenAt = result.takenAt?.toDate ? result.takenAt.toDate() : new Date(result.takenAt);
                              return (
                                <div key={result.id || idx} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{testName}</p>
                                    <p className="text-xs text-gray-500">{takenAt.toLocaleDateString()}</p>
                                  </div>
                                  <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                                    result.score >= 70 ? 'bg-emerald-100 text-emerald-700' : 
                                    result.score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {result.score}%
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Box Distribution */}
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Current Box Distribution</p>
                        <div className="grid grid-cols-4 gap-2">
                          {[1, 2, 3, 4].map((box) => (
                            <div key={box} className={`p-3 rounded-lg text-center border ${
                              box === 1 ? 'bg-red-50 border-red-200' :
                              box === 2 ? 'bg-amber-50 border-amber-200' :
                              box === 3 ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'
                            }`}>
                              <p className="text-xs text-gray-600">Box {box}</p>
                              <p className={`text-xl font-bold ${
                                box === 1 ? 'text-red-600' : box === 2 ? 'text-amber-600' : box === 3 ? 'text-blue-600' : 'text-green-600'
                              }`}>{learner.boxStats[box]}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Overall Stats */}
                      <div className="pt-4 border-t grid grid-cols-3 gap-4 text-center">
                        <div><p className="text-sm text-gray-500">All-Time Attempts</p><p className="text-lg font-bold">{learner.totalAttempts}</p></div>
                        <div><p className="text-sm text-gray-500">All-Time Correct</p><p className="text-lg font-bold text-green-600">{learner.totalCorrect}</p></div>
                        <div><p className="text-sm text-gray-500">Overall Accuracy</p><p className="text-lg font-bold text-indigo-600">{learner.accuracy}%</p></div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Period Test Scores */}
        {periodTestResults.length > 0 && (
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b bg-emerald-50">
              <h3 className="font-semibold text-emerald-900 flex items-center gap-2">
                <Award className="w-5 h-5" />
                Test Scores This Period
                <span className="ml-auto text-sm font-normal text-emerald-700">
                  {testTotals.count} tests · Avg: {testTotals.avgScore}%
                </span>
              </h3>
            </div>
            <div className="divide-y max-h-64 overflow-y-auto">
              {periodTestResults.map((result, idx) => {
                const profile = userProfiles[result.userId];
                const displayName = profile?.displayName || profile?.email || result.userId?.slice(0, 8);
                const testName = tests?.find(t => t.id === result.testId)?.name || 'Test';
                const takenAt = result.takenAt?.toDate ? result.takenAt.toDate() : new Date(result.takenAt);
                
                return (
                  <div key={result.id || idx} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-emerald-600 font-semibold text-sm">{displayName?.[0]?.toUpperCase() || '?'}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{displayName}</p>
                        <p className="text-xs text-gray-500">{testName} · {takenAt.toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className={`text-lg font-bold ${result.score >= 70 ? 'text-emerald-600' : result.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {result.score}%
                        </p>
                        <p className="text-xs text-gray-500">{result.correctAnswers}/{result.totalQuestions}</p>
                      </div>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        result.score >= 70 ? 'bg-emerald-100' : result.score >= 50 ? 'bg-amber-100' : 'bg-red-100'
                      }`}>
                        {result.score >= 70 ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        ) : result.score >= 50 ? (
                          <Clock className="w-5 h-5 text-amber-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



const StatCard = ({ icon: Icon, label, value, color = 'gray' }) => (
  <div className="bg-white p-4 rounded-xl border shadow-sm">
    <div className={`flex items-center gap-2 mb-1 ${color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-600' : 'text-gray-600'}`}>
      <Icon className="w-4 h-4" />
      <span className="text-sm">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
  </div>
);

export default PerformanceSummaryView;